import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { UseCompanyWorkspaces } from "./company";
import { buildCompanyScoresByDay } from "./kpi";
import useTotalRevenue, { findTotalRevenueKpi } from "../components/GoalProgress/useTotalRevenue";
import { hoursWorkedKpiOf } from "../components/LaborMetrics/data";
import { metaByIdFromDefs } from "../helpers/scoreAggregate";
import { findRevenuePerHourTwin } from "../helpers/derivedGoal";
import { twinGoalFor } from "../components/CustomWidget/yetiPace";
import useBeatYetiRate from "./useBeatYetiRate";
import { AVG_DAILY_REVENUE_ID, TECH_REVENUE_PER_HOUR_ID, BEAT_YETI_RATE_ID } from "../helpers/derivedMetricIds";
import {
  fetchWorkspaceScores, monthsForWindow, resolveWindow, sumScores,
} from "../pages/CompanyMetrics/dashboards/useWorkspaceData";

/* The "derived" metrics for Build Your Own — figures computed from data the app
   already holds (the company's canonical Total Revenue KPI, Technicians' hours,
   the live board roster vs the Yeti's pace), not real KPI documents of their
   own. No workspace an admin scopes a card to has one of these as an actual
   metric to pick, so they need fixed, reserved ids ScopedCustomWidget can
   recognise on sight rather than resolving through the normal by-id / by-name
   KPI lookup. */
export {
  AVG_DAILY_REVENUE_ID, TECH_REVENUE_PER_HOUR_ID, BEAT_YETI_RATE_ID, DERIVED_METRIC_IDS, isDerivedMetricId,
} from "../helpers/derivedMetricIds";

/** Mon–Fri days between two moments, inclusive. */
const businessDaysInRange = (from, to) => {
  if (!from?.isValid?.() || !to?.isValid?.()) return 0;
  let count = 0;
  const d = from.clone().startOf("day");
  const end = to.clone().startOf("day");
  while (d.isSameOrBefore(end, "day")) {
    const dow = d.day();
    if (dow > 0 && dow < 6) count += 1;
    d.add(1, "day");
  }
  return count;
};

/** One kpiId's amount per day across [from, to] — the same fold
    useTotalRevenue's own dailySeries uses, generalised for reuse here. */
const dailyAmountsOf = (scores, kpiId, from, to) => {
  const out = [];
  if (!kpiId || !from || !to || !from.isValid() || !to.isValid()) return out;
  const byDay = {};
  const cursor = from.clone().startOf("month");
  const lastMonth = to.clone().startOf("month");
  let guard = 0;
  while (cursor.isSameOrBefore(lastMonth, "month") && guard < 36) {
    Object.assign(byDay, buildCompanyScoresByDay(scores, cursor));
    cursor.add(1, "month");
    guard += 1;
  }
  const day = from.clone().startOf("day");
  const end = to.clone().startOf("day");
  let dayGuard = 0;
  while (day.isSameOrBefore(end, "day") && dayGuard < 400) {
    const key = day.format("YYYY-MM-DD");
    const cell = byDay[key]?.[kpiId];
    out.push({ date: key, value: typeof cell?.amt === "number" ? cell.amt : 0 });
    day.add(1, "day");
    dayGuard += 1;
  }
  return out;
};

/* The goal a twin metric lends a derived tile — see twinGoalFor. */
const goalFromTwin = (twin, bucket, custom, holidays) => {
  if (!twin) return null;
  const win = bucket === "year" ? "ytd" : (bucket || "month");
  const { from, to } = resolveWindow(win, custom);
  return twinGoalFor(twin, win, from, to, holidays);
};

/**
 * Live amounts for whichever derived ids are actually in use on the card.
 * `bucket`/`custom` are the SAME resolved window the rest of the card reads
 * (ScopedCustomWidget's `displayWin`/`displayCustom`), so switching the
 * board's date filter moves these the same as every other tile.
 *
 * `scopeWorkspaceIds` is the card's own scope (ScopedCustomWidget's `reading`).
 * The revenue pair ignore it on purpose — they read the company's canonical
 * Total Revenue KPI wherever it lives. Beat the Yeti rate is a question about a
 * ROSTER, so it does follow the scope, and falls back to the whole company when
 * the card has no workspaces of its own to point at.
 *
 * `byTeam` asks for Beat the Yeti to be broken out per workspace ("Technicians
 * 7/10"). The teams are exactly the workspaces the headline reads, so the rows
 * always add up to the big number; they come back on the tile as `teams`.
 */
export default function useDerivedRevenueMetrics(neededIds, bucket, custom, scopeWorkspaceIds = [], byTeam = false) {
  const companyId = useSelector((s) => s.authReducer.userDetails?.companyId);
  const companyKpis = useSelector((s) => s.kpiReducer.companyKpis);
  const rawWorkspaces = UseCompanyWorkspaces(true);
  const workspaces = useMemo(() => rawWorkspaces || [], [rawWorkspaces]);

  const needAvg = neededIds.includes(AVG_DAILY_REVENUE_ID);
  const needTech = neededIds.includes(TECH_REVENUE_PER_HOUR_ID);
  const needYeti = neededIds.includes(BEAT_YETI_RATE_ID);

  const revKpi = useMemo(() => findTotalRevenueKpi(companyKpis, workspaces), [companyKpis, workspaces]);
  const revenue = useTotalRevenue(revKpi, bucket, custom);

  /* Empty unless the metric is actually on the card — useBeatYetiRate fetches
     nothing for an empty scope, so an unused derived id costs nothing. */
  const yetiScopeIds = useMemo(() => {
    if (!needYeti) return [];
    const picked = (scopeWorkspaceIds || []).filter(Boolean).map(String);
    if (picked.length) return picked;
    return workspaces.map((w) => String(w?._id || "")).filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needYeti, (scopeWorkspaceIds || []).filter(Boolean).map(String).join("|"), workspaces]);
  const yetiTeams = useMemo(
    () => (needYeti && byTeam ? yetiScopeIds : []),
    [needYeti, byTeam, yetiScopeIds],
  );
  const beatYeti = useBeatYetiRate(yetiScopeIds, bucket, custom, yetiTeams);

  const techWs = useMemo(() => workspaces.find((w) => /technician/i.test(w?.name || "")), [workspaces]);
  const techHoursKpiId = techWs ? hoursWorkedKpiOf(companyKpis, techWs._id) : null;

  /* The goal a derived tile borrows from a real anchor metric that measures the
     same thing (see helpers/derivedGoal). Only looked up when the tile is on
     the card, and only once both source KPIs are known. */
  const holidays = useSelector((s) => s.companyReducer.holidays);
  const twin = useMemo(
    () => (needTech ? findRevenuePerHourTwin(companyKpis, revKpi?.kpiId, techHoursKpiId) : null),
    [needTech, companyKpis, revKpi?.kpiId, techHoursKpiId],
  );

  const months = useMemo(() => monthsForWindow(bucket, custom), [bucket, custom?.from, custom?.to]);
  const [hoursScores, setHoursScores] = useState(null);
  const hoursFetchKey = `${companyId || ""}|${techWs?._id || ""}|${techHoursKpiId || ""}|${months.join(",")}`;
  useEffect(() => {
    if (!needTech || !companyId || !techWs || !techHoursKpiId) { setHoursScores(null); return undefined; }
    let alive = true;
    fetchWorkspaceScores(companyId, techWs._id, months)
      .then((s) => { if (alive) setHoursScores(s || []); })
      .catch(() => { if (alive) setHoursScores([]); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needTech, companyId, hoursFetchKey]);

  return useMemo(() => {
    const out = {};
    const { from, to } = resolveWindow(bucket, custom);
    if (needAvg) {
      const days = businessDaysInRange(from, to);
      out[AVG_DAILY_REVENUE_ID] = {
        amount: days > 0 && revenue.active ? revenue.amount / days : 0,
        kind: "$",
        title: "Average Daily Revenue",
        loading: !!revenue.loading,
        /* Chart shows the real day-by-day revenue that fed the average, not
           the average repeated — that's what carries actual information. */
        dailySeries: revenue.dailySeries || [],
      };
    }
    if (needTech) {
      let hours = 0;
      let hoursDaily = [];
      if (techWs && techHoursKpiId && hoursScores) {
        const defs = (companyKpis && companyKpis[techWs._id]) || [];
        const { perKpi } = sumScores(hoursScores, from, metaByIdFromDefs(defs), to);
        hours = Number(perKpi?.[techHoursKpiId]?.amt) || 0;
        hoursDaily = dailyAmountsOf(hoursScores, techHoursKpiId, from, to);
      }
      const revenueDaily = revenue.dailySeries || [];
      const hoursByDay = {};
      hoursDaily.forEach((d) => { hoursByDay[d.date] = d.value; });
      const dailySeries = revenueDaily.map((d) => {
        const h = hoursByDay[d.date] || 0;
        return { date: d.date, value: h > 0 ? d.value / h : 0 };
      });
      out[TECH_REVENUE_PER_HOUR_ID] = {
        amount: hours > 0 && revenue.active ? revenue.amount / hours : 0,
        kind: "$",
        title: techWs ? `${techWs.name} Revenue per Hour` : "Technicians' Revenue per Hour",
        loading: !!revenue.loading || (!!techHoursKpiId && !hoursScores),
        dailySeries,
        goal: goalFromTwin(twin, bucket, custom, holidays),
      };
    }
    if (needYeti) {
      const nameOf = (id) => (workspaces.find((w) => String(w?._id) === String(id))?.name || "Workspace");
      out[BEAT_YETI_RATE_ID] = {
        /* One entry per team the card was asked to show, in the order picked.
           `loading` is the read's own: until it lands every count is a
           placeholder 0 of 0, which must not be printed as a real result. */
        teams: yetiTeams.map((id) => ({
          workspaceId: id,
          name: nameOf(id),
          loading: !!beatYeti.loading,
          beating: beatYeti.byWorkspace?.[id]?.beating || 0,
          players: beatYeti.byWorkspace?.[id]?.players || 0,
          rate: beatYeti.byWorkspace?.[id]?.rate || 0,
        })),
        /* A 0–1 fraction, the app's storage form for every rate — the tile's
           own formatter is what prints it as a percentage. */
        amount: beatYeti.amount,
        /* The head-count behind the rate — a step bonus on it says "2 more
           players" rather than "8 more points". */
        beating: beatYeti.beating || 0,
        players: beatYeti.players || 0,
        kind: "%",
        title: "Beat the Yeti rate",
        loading: !!beatYeti.loading,
        /* No day-by-day series: "who is ahead of the Yeti" is a standing, as-of
           reading, and the Yeti's pace is only defined for a whole window — a
           per-day rate would be a different (and much noisier) statistic than
           the headline number. With no series the tile reads against its 100%
           target, exactly like every other rate metric that has no daily
           history (Closed Rate, Bid Accuracy). */
        dailySeries: [],
      };
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    needAvg, needTech, needYeti, revenue.amount, revenue.active, revenue.loading,
    hoursScores, techWs, techHoursKpiId, bucket, custom?.from, custom?.to, companyKpis, twin, holidays,
    beatYeti.amount, beatYeti.beating, beatYeti.players, beatYeti.loading, beatYeti.byWorkspace, yetiTeams, workspaces,
  ]);
}
